/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import {
  createContext,
  type ReactNode,
  useCallback,
  use,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import type { EmailPromoState, OptInValues, PromoContent } from '../features/emailTab/interface';
import { useLabels } from '@/hooks/useLabels';
import { useAppData } from '@/hooks/useAppData';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { routePaths } from '@/constants/routePaths';
import { localStorage } from '@/utils/localStorage';
import { useMyProfile } from '@/features/myProfile/api/getMyProfileV2';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useLocale } from '@/hooks/useLocale';

type ModalOPPromptContextType = {
  emailPromptAlertType: boolean;
  isUserPending: boolean;
  promoState: EmailPromoState;
  updatePromoState: (updater: Partial<EmailPromoState>) => void;
  handleTogglePromo: VoidFunction;
  shouldHideComponent: boolean;
  offer: PromoContent | null;
  promoPrint: boolean;
  optInValues: OptInValues[];
};

const ModalOPPromptContext = createContext<ModalOPPromptContextType>(
  {} as ModalOPPromptContextType,
);

const labelMap = {
  lblPromoDiscount1: 'label_email_promo_discount1',
  lblPromoDiscount2: 'label_email_promo_discount2',
  lblPromoDiscountText1: 'label_email_promo_discount_text1',
  lblPromoDiscountText2: 'label_email_promo_discount_text2',
  lblPromoValue1: 'label_email_promo_value1',
  lblPromoValue2: 'label_email_promo_value2',
  lblpromoCode1: 'label_email_promo_code1',
  lblpromoCode2: 'label_email_promo_code2',
  lblPromoExpirationDate1: 'label_promo_expiration_date1',
  lblPromoExpirationDate2: 'label_promo_expiration_date2',
};

export const useModalOPPromptContext = () => {
  const context = use(ModalOPPromptContext);
  if (context === null) {
    throw new Error('useModalOPPromptContext must be used within a ModalOPPromptProvider');
  }
  return context;
};

type ModalOPPromptProviderProps = {
  children: ReactNode;
};

export const ModalOPPromptProvider = ({ children }: ModalOPPromptProviderProps) => {
  const [promoState, setPromoState] = useState<EmailPromoState>({
    isOpen: false,
    isClosed: false,
    isConfirmed: false,
    isModalOpened: false,
    optedFor: [],
  });
  const [offer, setOffer] = useState<PromoContent | null>(null);
  const [showSideTab, setShowSideTab] = useState(false);
  const [hiddenTab, setHiddenTab] = useState(true);
  const { data: currentUser, isPending: isUserPending, isError } = useMyProfile();
  const labels = useLabels(labelMap);
  const router = useLegacyRouter();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.only('sm'));
  const { appData } = useAppData();
  const { userAuthenticated } = appData;
  const locale = useLocale();
  const isUS = locale === 'en-US';
  const showModal = isUS;
  const onRenderCheck = useRef(false);

  const promoPrint = !!currentUser?.optins.emailOptins?.includes('PROMO');

  const optInValues: OptInValues[] = useMemo(() => {
    const values: OptInValues[] = [];
    if (!currentUser?.optins.emailOptins?.includes('PROMO')) {
      values.push('EMAIL');
    }
    if (!currentUser?.optins.telephoneOptins?.includes('PROMO')) {
      values.push('SMS');
    }
    return values;
  }, [currentUser?.optins.emailOptins, currentUser?.optins.telephoneOptins]);

  const emailPromptAlertType = useMemo((): boolean => {
    if (!currentUser?.loyaltyAccountAlerts) {
      return false;
    }

    const { loyaltyAccountAlerts } = currentUser;

    const loyaltyAccountAlertPromptObject = loyaltyAccountAlerts.find(
      (alert) => alert.alertType === 'OP',
    );

    return loyaltyAccountAlertPromptObject?.status === 'PROMPT' || false;
  }, [currentUser]);

  useEffect(() => {
    if (optInValues.includes('EMAIL') && !showSideTab && userAuthenticated) {
      // if user has 'OP' alert, show first promo ($10 off $50)
      if (emailPromptAlertType) {
        setOffer({
          discount: labels.lblPromoDiscount1,
          discountText: labels.lblPromoDiscountText1,
          value: labels.lblPromoValue1,
          promoCode: labels.lblpromoCode1,
          expirationDate: labels.lblPromoExpirationDate1,
        });
      }

      // if user don't have any alert, show second promo ($20 off $100)
      if (!emailPromptAlertType) {
        setOffer({
          discount: labels.lblPromoDiscount2,
          discountText: labels.lblPromoDiscountText2,
          value: labels.lblPromoValue2,
          promoCode: labels.lblpromoCode2,
          expirationDate: labels.lblPromoExpirationDate2,
        });
      }
    } else if (!userAuthenticated && showSideTab) {
      setShowSideTab(false);
      setOffer(null);
      setPromoState({
        isOpen: false,
        isClosed: false,
        isConfirmed: false,
        isModalOpened: false,
        optedFor: [],
      });
    }
  }, [optInValues, labels, emailPromptAlertType, showSideTab, userAuthenticated]);

  useEffect(() => {
    const closed = promoState.isClosed && !promoState.isModalOpened;
    const hasOptedIn = optInValues.length === 0 && promoState.isConfirmed;
    const isHidden = localStorage.getItem('displayEmailOptInAlert') === 'false';

    if (!onRenderCheck.current || (hiddenTab !== isHidden && closed)) {
      onRenderCheck.current = true;
      setHiddenTab(isHidden);
    }

    const shouldShow =
      !hiddenTab &&
      (optInValues.length > 0 || hasOptedIn) &&
      userAuthenticated &&
      !closed &&
      !isUserPending &&
      !isError &&
      offer !== null;

    setShowSideTab(shouldShow);
  }, [
    promoState,
    isUserPending,
    emailPromptAlertType,
    optInValues.length,
    offer,
    userAuthenticated,
    showModal,
    showSideTab,
    hiddenTab,
    isError,
  ]);

  const updatePromoState = useCallback((updater: Partial<EmailPromoState>) => {
    setPromoState((state) => ({ ...state, ...updater }));
  }, []);

  const shouldHideComponent = useMemo(() => {
    const prohibitedRoutes = [routePaths.checkout, routePaths.cart];
    const isProhibitedRoute = prohibitedRoutes.some((route) => router.asPath.includes(route));
    const isMobileProfilePage = isMobile && router.asPath === routePaths.userSettings;

    if (isProhibitedRoute) {
      return true;
    }

    if (isMobileProfilePage) {
      return true;
    }

    if (showSideTab) {
      return false;
    }
    return true;
  }, [showSideTab, router.asPath, isMobile]);

  const handleTogglePromo = useCallback(() => {
    const newPromoState: Partial<EmailPromoState> = showModal
      ? { isModalOpened: !promoState.isModalOpened }
      : { isOpen: !promoState.isOpen };
    updatePromoState(newPromoState);
  }, [promoState.isModalOpened, promoState.isOpen, showModal, updatePromoState]);

  return (
    <ModalOPPromptContext
      value={{
        emailPromptAlertType,
        isUserPending,
        promoState,
        updatePromoState,
        handleTogglePromo,
        shouldHideComponent,
        offer,
        promoPrint,
        optInValues,
      }}
    >
      {children}
    </ModalOPPromptContext>
  );
};
